import { ReactNode } from "react";
import { Button, Card, Form } from "react-bootstrap";
import FormFieldLabel from "@components/Forms/FormFieldLabel/FormFieldLabel";
import SearchField from "@components/Forms/SearchField/SearchField";
import { BatchActionPill, BatchAction } from "./BatchActionPill";
import "./CatalogIndex.css";

export interface AdminListCardSearchProps {
  placeholder: string;
  value: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onSubmit: (e: React.FormEvent<HTMLFormElement>) => void;
  onClear: () => void;
  label?: string;
  /** Right-aligned on the label row, e.g. a "Show mine only" toggle. */
  labelAccessory?: ReactNode;
  /** Beside the search field on its row, top-aligned, e.g. filter icons. */
  trailing?: ReactNode;
}

export interface AdminListCardSecondaryAction {
  label: string;
  onClick: () => void;
  disabled?: boolean;
}

export interface AdminListCardProps {
  title: string;
  onNew?: () => void;
  newButtonLabel?: string;
  secondaryAction?: AdminListCardSecondaryAction;
  searchProps?: AdminListCardSearchProps;
  batchActions?: BatchAction[];
  selectedCount?: number;
  /** Renders in the same flex row as the batch pill — sits alone at the
   *  left when nothing is selected, and after the pill (bumped right) once
   *  something is. E.g. a running total that should occupy the pill's slot. */
  extraInfo?: ReactNode;
  /** Renders without the card border/shadow and title — for a list nested
   *  inside an AdminFormPage tab, where the tab bar already serves as the
   *  header. */
  bare?: boolean;
  /** bare only: hides `children` and shows `emptyMessage` in the header row
   *  instead of the batch pill — mirrors Edit Blueprint's item tabs
   *  (Products/Resources/Governance/Expense). */
  isEmpty?: boolean;
  emptyMessage?: string;
  children: ReactNode;
}

export const AdminListCard = ({
  title,
  onNew,
  newButtonLabel = "+ New",
  secondaryAction,
  searchProps,
  batchActions,
  selectedCount = 0,
  extraInfo,
  bare = false,
  isEmpty = false,
  emptyMessage,
  children,
}: AdminListCardProps) => {
  const showBatchPill =
    !!batchActions && batchActions.length > 0 && selectedCount > 0;
  const showActionRow = showBatchPill || !!searchProps || !!extraInfo;

  const actionButtons = (secondaryAction || onNew) && (
    <div className="admin-list-card__title-actions">
      {secondaryAction && (
        <Button
          className="btnSeawhite"
          onClick={secondaryAction.onClick}
          disabled={secondaryAction.disabled}
        >
          {secondaryAction.label}
        </Button>
      )}
      {onNew && (
        <Button className="squareGreenButton" onClick={onNew}>
          {newButtonLabel}
        </Button>
      )}
    </div>
  );

  const actionRow = showActionRow && (
    <div className="admin-list-card__action-row">
      {showBatchPill && (
        <BatchActionPill
          selectedCount={selectedCount}
          actions={batchActions!}
        />
      )}
      {extraInfo}
      {searchProps && (
        <div className="admin-list-card__search-wrapper">
          <div
            className={`admin-list-card__search-label-row${
              searchProps.labelAccessory
                ? " admin-list-card__search-row--split"
                : ""
            }`}
          >
            <FormFieldLabel
              label={searchProps.label ?? "Search"}
              className={undefined as unknown as string}
            />
            {searchProps.labelAccessory && (
              <div className="admin-list-card__search-accessory">
                {searchProps.labelAccessory}
              </div>
            )}
          </div>
          <div
            className={`admin-list-card__search-field-row${
              searchProps.trailing ? " admin-list-card__search-row--split" : ""
            }`}
          >
            <Form
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  searchProps.onSubmit(
                    e as unknown as React.FormEvent<HTMLFormElement>
                  );
                }
              }}
              onSubmit={searchProps.onSubmit}
            >
              <SearchField
                placeholder={searchProps.placeholder}
                value={searchProps.value}
                onChange={searchProps.onChange}
                onClick={searchProps.onClear}
                fullWidth
              />
            </Form>
            {searchProps.trailing && (
              <div className="admin-list-card__search-trailing">
                {searchProps.trailing}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );

  if (bare) {
    const showEmptyMessage = isEmpty && !showBatchPill && !!emptyMessage;
    const hasLeftSlot = showBatchPill || showEmptyMessage;

    return (
      <div className="admin-list-card admin-list-card--bare">
        <div
          className={`admin-list-card__title-row admin-list-card__title-row--bare${
            hasLeftSlot ? " admin-list-card__title-row--split" : ""
          }`}
        >
          {showBatchPill ? (
            <BatchActionPill
              selectedCount={selectedCount}
              actions={batchActions!}
            />
          ) : showEmptyMessage ? (
            <div className="admin-list-card__empty">{emptyMessage}</div>
          ) : null}
          {actionButtons}
        </div>
        {searchProps && (
          <div className="admin-list-card__action-row">
            <div className="admin-list-card__search-wrapper">
              <FormFieldLabel
                label={searchProps.label ?? "Search"}
                className={undefined as unknown as string}
              />
              <Form
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    searchProps.onSubmit(
                      e as unknown as React.FormEvent<HTMLFormElement>
                    );
                  }
                }}
                onSubmit={searchProps.onSubmit}
              >
                <SearchField
                  placeholder={searchProps.placeholder}
                  value={searchProps.value}
                  onChange={searchProps.onChange}
                  onClick={searchProps.onClear}
                  fullWidth
                />
              </Form>
            </div>
          </div>
        )}
        {!isEmpty && children}
      </div>
    );
  }

  return (
    <div className="admin-list-card legacyPageBleed">
      <Card>
        <Card.Header>
          <div className="admin-list-card__title-row">
            <div className="adminListCardTitle">{title}</div>
            {actionButtons}
          </div>
          <hr />
          {actionRow}
        </Card.Header>
        {children}
      </Card>
    </div>
  );
};
